import React, { useEffect, useRef, useState } from 'react';
import jsVectorMap from 'jsvectormap';
import 'jsvectormap/dist/maps/world.js';
import { getAnalyticsData } from '../../utils/visitorTracking';
import { getIPGeolocation, getMultipleIPGeolocations } from '../../utils/ipGeolocation';
import './VisitorMap.css';

const VisitorMap = () => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [visitorLocations, setVisitorLocations] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadVisitorLocations();
  }, []);

  const loadVisitorLocations = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get analytics data from server
      const analyticsData = await getAnalyticsData();
      const visitorsByIP = analyticsData.visitorsByIP || [];
      const uniqueIPs = [...new Set(visitorsByIP.map(v => v.ip))];
      
      if (uniqueIPs.length === 0) {
        setVisitorLocations([]);
        setLoading(false);
        return;
      }
      
      // Get geolocation for each IP (with delay to respect rate limits)
      const locations = await getMultipleIPGeolocations(uniqueIPs, 1500);
      
      // Combine with visit count data
      const locationsWithCounts = locations.map(loc => {
        const visitorData = visitorsByIP.find(v => v.ip === loc.ip);
        return {
          ...loc,
          visitCount: visitorData ? visitorData.totalVisits : 1,
          routes: visitorData ? visitorData.routes : []
        };
      });
      
      setVisitorLocations(locationsWithCounts);
      setLoading(false);
    } catch (err) {
      console.error('Error loading visitor locations:', err);
      setError('Failed to load visitor locations');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!mapRef.current || loading) return;

    // Destroy existing map if it exists
    if (mapInstanceRef.current) {
      mapInstanceRef.current.destroy();
    }

    // Create markers from visitor locations
    const markers = visitorLocations
      .filter(loc => loc.lat && loc.lon)
      .map((loc, index) => ({
        name: `${loc.city || loc.country}, ${loc.country}`,
        coords: [loc.lat, loc.lon],
        style: { 
          fill: getMarkerColor(loc.visitCount),
          r: getMarkerSize(loc.visitCount)
        },
        visitCount: loc.visitCount,
        ip: loc.ip,
        city: loc.city,
        country: loc.country
      }));

    // Create the map
    const map = new jsVectorMap({
      selector: '#visitor-map',
      map: 'world',
      backgroundColor: '#f8f9fa',
      regionStyle: {
        initial: {
          fill: '#e0e0e0',
          stroke: '#ffffff',
          strokeWidth: 1,
        },
        hover: {
          fill: '#c0c0c0',
          fillOpacity: 1,
          cursor: 'pointer',
        },
      },
      markers: markers,
      markerStyle: {
        initial: {
          stroke: '#ffffff',
          strokeWidth: 2,
        },
        hover: {
          stroke: '#ffffff',
          strokeWidth: 3,
          cursor: 'pointer',
        },
      },
      markerLabelStyle: {
        initial: {
          fontFamily: 'Inter, sans-serif',
          fontSize: 12,
          fontWeight: 'bold',
          fill: '#333',
        },
      },
      regionLabelStyle: {
        initial: {
          fontFamily: 'Inter, sans-serif',
          fontSize: 12,
          fill: '#333',
        },
      },
      zoomButtons: true,
      zoomOnScroll: true,
      zoomMax: 10,
      zoomMin: 1,
      onMarkerTooltipShow(event, tooltip, index) {
        if (markers[index]) {
          const marker = markers[index];
          const tooltipText = `${marker.city || marker.country}, ${marker.country}\nIP: ${marker.ip}\nVisits: ${marker.visitCount}`;
          tooltip.text(tooltipText, true);
        }
      },
    });

    mapInstanceRef.current = map;

    // Apply colors to markers after map is rendered
    setTimeout(() => {
      const mapMarkers = document.querySelectorAll('#visitor-map circle[class*="jsvectormap-marker"]');
      mapMarkers.forEach((marker, index) => {
        if (markers[index]) {
          const markerData = markers[index];
          marker.setAttribute('fill', markerData.style.fill);
          marker.style.fill = markerData.style.fill;
          marker.setAttribute('r', markerData.style.r);
        }
      });
    }, 100);

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.destroy();
        mapInstanceRef.current = null;
      }
    };
  }, [visitorLocations, loading]);

  // Get marker color based on visit count
  const getMarkerColor = (visitCount) => {
    if (visitCount >= 50) return '#dc2626'; // Red for high traffic
    if (visitCount >= 20) return '#f57919'; // Orange for medium-high
    if (visitCount >= 10) return '#0fbde7'; // Blue for medium
    if (visitCount >= 5) return '#32cd32'; // Green for low-medium
    return '#667eea'; // Purple for low
  };

  // Get marker size based on visit count
  const getMarkerSize = (visitCount) => {
    if (visitCount >= 50) return 10;
    if (visitCount >= 20) return 8;
    if (visitCount >= 10) return 7;
    if (visitCount >= 5) return 6;
    return 5;
  };

  return (
    <div className="visitor-map-wrapper">
      <div className="visitor-map-header">
        <h3>Visitor Locations Map</h3>
        <button onClick={loadVisitorLocations} className="refresh-btn" disabled={loading}>
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      
      {loading ? (
        <div className="loading-message">
          <p>Loading visitor locations...</p>
          <p className="loading-hint">This may take a moment as we fetch location data for each IP address.</p>
        </div>
      ) : (
        <>
          <div ref={mapRef} id="visitor-map" className="jsvectormap-container"></div>
          
          {visitorLocations.length === 0 ? (
            <div className="no-data-message">
              <p>No visitor location data available yet.</p>
              <p>Visitor locations will appear here as visitors access your site.</p>
            </div>
          ) : (
            <div className="map-legend">
              <h4>Visit Count Legend</h4>
              <div className="legend-items">
                <div className="legend-item">
                  <span className="legend-color" style={{ background: '#dc2626' }}></span>
                  <span>50+ visits</span>
                </div>
                <div className="legend-item">
                  <span className="legend-color" style={{ background: '#f57919' }}></span>
                  <span>20-49 visits</span>
                </div>
                <div className="legend-item">
                  <span className="legend-color" style={{ background: '#0fbde7' }}></span>
                  <span>10-19 visits</span>
                </div>
                <div className="legend-item">
                  <span className="legend-color" style={{ background: '#32cd32' }}></span>
                  <span>5-9 visits</span>
                </div>
                <div className="legend-item">
                  <span className="legend-color" style={{ background: '#667eea' }}></span>
                  <span>1-4 visits</span>
                </div>
              </div>
              <div className="visitor-count-info">
                <p>Total Locations: {visitorLocations.length}</p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default VisitorMap;

